import React, { useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { CheckCircle, ClipboardList, Users, BookOpen, Truck, BarChart3, Headphones, ArrowRight, ShieldCheck, Zap } from "lucide-react";
import { motion } from "framer-motion";
import gsap from "gsap";

const PROCESS_IMG = "/assets/atf_hero_bg.png"; // Repurposing elite tactical generated image for process

const ProcessCard = ({ step }) => {
  const cardRef = useRef(null);
  const bgRef = useRef(null);
  const titleRef = useRef(null);
  const descRef = useRef(null);
  const numberRef = useRef(null);
  const iconRef = useRef(null);
  const iconBoxRef = useRef(null);
  const tweenRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      tweenRef.current = gsap.timeline({ paused: true })
        // Smoothly and slowly expand the background from the bottom left
        .to(bgRef.current, { 
          clipPath: "circle(150% at 0% 100%)", 
          duration: 1.0, 
          ease: "power2.inOut" 
        }, 0)
        // Transition content colors smoothly alongside the expansion
        .to(titleRef.current, { color: "#ffffff", duration: 0.8, ease: "power2.out" }, 0.2)
        .to(descRef.current, { color: "rgba(255, 255, 255, 0.5)", duration: 0.8, ease: "power2.out" }, 0.2)
        .to(numberRef.current, { color: "rgba(255, 255, 255, 0.05)", duration: 0.8, ease: "power2.out" }, 0)
        .to(iconBoxRef.current, { backgroundColor: "#835F21", duration: 0.8, ease: "power2.out" }, 0)
        .to(iconRef.current, { color: "#ffffff", duration: 0.8, ease: "power2.out" }, 0);
    }, cardRef);

    return () => ctx.revert();
  }, []);

  const handleMouseEnter = () => tweenRef.current?.play();
  const handleMouseLeave = () => tweenRef.current?.reverse();

  return (
    <div 
      ref={cardRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className="h-full bg-[#F9F8F5] p-12 rounded-[2.5rem] relative overflow-hidden border border-[#141414]/5 shadow-[0_40px_80px_rgba(0,0,0,0.02)] transition-transform duration-700 hover:-translate-y-2 flex flex-col cursor-pointer"
    >
      <div 
        ref={bgRef} 
        className="absolute inset-0 bg-[#141414] z-0 pointer-events-none" 
        style={{ clipPath: "circle(0% at 0% 100%)" }}
      />
      <div className="relative z-10 flex flex-col h-full pointer-events-none">
        <div className="flex items-start justify-between mb-10">
          <div ref={iconBoxRef} className="w-16 h-16 rounded-2xl bg-[#835F21]/10 flex items-center justify-center">
            <step.icon ref={iconRef} size={28} className="text-[#835F21]" strokeWidth={1.5} />
          </div>
          <span ref={numberRef} className="font-display font-medium text-5xl text-[#141414]/5 leading-none">
            {step.number}
          </span>
        </div>
        <h3 ref={titleRef} className="font-display font-medium text-2xl text-[#141414] mb-4 tracking-tight">{step.title}</h3>
        <p ref={descRef} className="font-sans text-[15px] text-[#141414]/60 leading-relaxed font-light flex-grow">{step.desc}</p>
      </div>
    </div>
  );
};

const FadeUp = ({ children, delay = 0, className = "" }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: 0.8, delay: delay / 1000, ease: [0.16, 1, 0.3, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

const steps = [
  {
    icon: ClipboardList,
    number: "01",
    title: "Site Assessment",
    desc: "We begin with a detailed evaluation of your site, including entry and exit points, risk areas, and any existing security gaps. This initial assessment helps us determine the exact level of security required for your operations.",
  },
  {
    icon: Users,
    number: "02",
    title: "Deployment Planning",
    desc: "Based on the assessment, we plan the deployment by defining the number of personnel required, their roles and responsibilities, and the shift structure. This ensures the entire setup aligns with your operational needs.",
  },
  {
    icon: BookOpen,
    number: "03",
    title: "Personnel Allocation & Training",
    desc: "We assign trained and verified personnel and provide site-specific briefings covering layout, duties, and emergency procedures. This ensures every team member is fully prepared from day one.",
  },
  {
    icon: Truck,
    number: "04",
    title: "On-Ground Deployment",
    desc: "Personnel are deployed with a clear reporting structure, proper supervision, and defined protocols. Our team ensures smooth coordination and disciplined execution from the start.",
  },
  {
    icon: BarChart3,
    number: "05",
    title: "Monitoring & Reporting",
    desc: "We maintain daily operational control through structured monitoring systems, including attendance tracking, patrol logs, and incident reporting, ensuring full visibility and accountability for the client.",
  },
  {
    icon: Headphones,
    number: "06",
    title: "Continuous Supervision",
    desc: "Our team conducts regular site checks, monitors performance, and resolves issues promptly to maintain consistent service quality without disruption.",
  },
];

const standards = [
  "PSARA Licensed Operations",
  "Full Statutory Compliance (ESI, PF, Labor Laws)",
  "Police Verified Personnel",
  "Regular Site Reviews & Improvements",
];

export default function HowWeWork() {
  return (
    <main className="bg-[#F9F8F5] font-sans text-[#141414]" data-testid="how-we-work-page">
      {/* ── 01. Hero ── */}
      <section className="bg-[#141414] h-[85vh] md:h-[75vh] flex items-center relative overflow-hidden" data-testid="how-hero">
        <div className="absolute inset-0 z-0">
          {/* Split-Screen Slanted Overlay */}
          <div 
            className="absolute inset-y-0 left-0 w-full lg:w-[40%] bg-[#F9F8F5]/90 backdrop-blur-sm z-10 pointer-events-none" 
            style={{ clipPath: "polygon(0 0, 85% 0, 100% 100%, 0 100%)" }}
          />
          
          <div className="absolute inset-0 z-0">
            <img src="/assets/howwework.png" alt="Deployment Architecture" className="w-full h-full object-cover object-[80%_center] opacity-50" />
          </div>
          <div className="absolute inset-0 bg-[#835F21]/5 mix-blend-multiply z-10 pointer-events-none" />
        </div>
        
        <div className="relative z-20 w-full lg:w-[40%] px-6 md:px-12 lg:pl-24 lg:pr-12 py-12 md:py-16">
          <div className="relative z-10">
            <span className="section-label block mb-6 text-[#835F21]">Deployment Model</span>
            <h1 className="font-display font-medium text-[#141414] leading-[1.05] mb-8 tracking-tight"
                style={{ fontSize: "clamp(20px, 3vw, 44px)" }}
            >
              Structured
              <span className="text-[#835F21] italic font-light mt-2 block">Execution.</span>
            </h1>
            <p className="font-sans text-lg md:text-xl text-[#141414]/70 leading-relaxed font-light">
             We follow a clear, step-by-step deployment process to ensure every site is secured efficiently and managed consistently.
            </p>
          </div>
        </div>
      </section>

      {/* ── 02. Six-Step Process ── */}
      <section className="bg-white py-32 md:py-48" data-testid="how-we-work-steps">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:items-center text-center mb-24">
            <FadeUp className="max-w-3xl">
              <span className="font-sans text-[11px] font-bold tracking-[0.2em] text-[#835F21] uppercase block mb-6">Our Working Process</span>
              <h2 className="font-display font-medium text-[#141414] leading-[1.05] tracking-tight" style={{ fontSize: "clamp(40px, 5vw, 64px)" }}>
                A Simple & Proven <br /> <span className="italic font-light text-[#835F21]">Approach</span>
              </h2>
            </FadeUp>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {steps.map((step, i) => (
              <FadeUp key={i} delay={i * 100} className="h-full">
                <ProcessCard step={step} />
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* ── 03. Institutional Protocol ── */}
      <section className="bg-[#141414] py-32 md:py-48 relative overflow-hidden" data-testid="how-we-work-standards">
        <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-[#835F21]/20 rounded-full blur-[150px] opacity-20 pointer-events-none translate-x-1/2 -translate-y-1/2" />
        
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-20 lg:gap-32 items-center relative z-10">
          <FadeUp>
            <span className="font-sans text-[11px] font-bold tracking-[0.2em] text-[#835F21] uppercase block mb-6">Our Commitment</span>
            <h2 className="font-display font-medium text-white leading-[1.05] mb-8 tracking-tight" style={{ fontSize: "clamp(40px, 5vw, 64px)" }}>
              Accountability <br /> <span className="italic font-light text-[#835F21]">at Every Level.</span>
            </h2>
            <p className="font-sans text-lg text-white/50 leading-relaxed mb-12 font-light">
              We operate with a strong focus on compliance, discipline, and transparency.
            </p>
            <div className="grid grid-cols-1 gap-6">
              {standards.map((s, i) => (
                <div key={i} className="flex items-center gap-6 border border-white/10 bg-white/5 backdrop-blur-sm rounded-2xl p-6 hover:border-[#835F21]/50 hover:bg-[#835F21]/10 transition-colors group">
                  <CheckCircle size={24} className="text-[#835F21] flex-shrink-0" strokeWidth={1.5} />
                  <span className="font-sans text-[13px] font-medium text-white/90 tracking-wide group-hover:text-white transition-colors">{s}</span>
                </div>
              ))}
            </div>
          </FadeUp>

          <FadeUp delay={200} className="relative">
            <div className="relative rounded-[2.5rem] overflow-hidden shadow-2xl border border-white/10 group">
              <img src={PROCESS_IMG} alt="ATF Operational Framework" className="w-full aspect-[4/5] lg:aspect-square object-cover opacity-60 transition-transform duration-1000 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
              <div className="absolute inset-0 flex items-center justify-center p-8">
                <div className="bg-[#141414]/80 backdrop-blur-md border border-white/10 p-10 rounded-[2rem] text-white max-w-sm w-full shadow-2xl translate-y-8 group-hover:translate-y-0 transition-transform duration-700">
                  <h3 className="font-display font-medium italic text-3xl mb-6 leading-tight tracking-tight">
                    "Reliable security comes from consistent execution, not just deployment."
                  </h3>
                  <div className="w-12 h-[1px] bg-[#835F21]" />
                </div>
              </div>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* ── 04. Final CTA ── */}
      <section className="bg-[#835F21] py-24 md:py-32" data-testid="how-we-work-cta">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <FadeUp>
            <h2 className="font-display font-medium text-white leading-tight mb-10 tracking-tight" style={{ fontSize: "clamp(36px, 5vw, 64px)" }}>
              Initialize Your <span className="italic font-light">Strategic Audit</span>
            </h2>
            <Link to="/contact" className="btn-primary bg-white text-[#141414] hover:bg-[#F9F8F5] border-none shadow-[0_0_40px_rgba(255,255,255,0.15)] h-16 px-12 text-sm group tracking-[0.2em] uppercase">
              Deploy Protocol
              <ArrowRight size={18} className="ml-3 group-hover:translate-x-1 transition-transform" />
            </Link>
          </FadeUp>
        </div>
      </section>
    </main>
  );
}
